import {
  ajax
} from 'application/src/js/functions.js';

import {
  ConfirmOverlay,
  Mediator
} from 'application/src/js/util.js';

import {
  setHint
} from 'website/src/js/registration/shared/functions.js';

export default class Company {
  constructor(args) {
    this.translations = translations.registrationForm;

    this.container = document.querySelector('.cell.company');
    this.inputField = this.container.querySelector('input.company');
    this.hiddenAddressIdInputField = this.container.querySelector('input.global-address-id');
    this.hiddenCompanyIdInputField = this.container.querySelector('input.company-id');
    this.hintOut = this.container.querySelector('.hint');

    this.setHint = setHint.bind(this);

    this.errorClass = 'invalid';

    this.requestJoinCompanyConfirmOverlay = new ConfirmOverlay({
      container: this.form,
      headline: this.translations.requestJoinCompanyConfirmOverlay.headline,
      description: this.translations.requestJoinCompanyConfirmOverlay.description,
      buttonConfirmLabel: this.translations.requestJoinCompanyConfirmOverlay.confirm,
      buttonCancelLabel: this.translations.requestJoinCompanyConfirmOverlay.cancel,
      maxWidth: 600
    });

    this.inputSuggestions = new InputSuggestions({
      inputField: this.inputField
    });

    this.addEvents();
  }

  addEvents() {
    this.inputField.addEventListener('change', e => {
      this.inputField.classList.remove(this.errorClass);
      this.setHint();
    });

    this.inputField.addEventListener('input', e => {
      this.hiddenAddressIdInputField.value = '';
      this.hiddenCompanyIdInputField.value = '';
      this.setHint();

      if (e.target.value < 2) return;

      this.wait().then(_ => {
        ajax('/app/ajax/ajaxSearchAddress', {
          searchvalue: e.target.value,
          limit: 5
        }).then(response => {
            if (response.data.length === 1 && response.data[0].name.toLowerCase() === e.target.value.trim().toLowerCase()) {
              this.selectSuggestion(response.data[0], true);
              this.inputSuggestions.render();
            } else {
              this.inputSuggestions.render(response.data);
            }
          })
          .catch(e => {
            console.log(e);
          });
      });
    });

    this.inputSuggestions.subscribe('select', suggestionData => this.selectSuggestion(suggestionData));

    this.requestJoinCompanyConfirmOverlay.subscribe('confirm', overlay => {
      this.onConfirmJoinCompany();
      overlay.close();
    });

    this.requestJoinCompanyConfirmOverlay.subscribe('cancel', overlay => {
      this.inputField.value = '';
      this.hiddenAddressIdInputField.value = '';
      this.hiddenCompanyIdInputField.value = '';
      overlay.close();
    });
  }

  onConfirmJoinCompany() {
    this.setHint({
      type: 'info',
      text: this.translations.joinCompanyInfo
    });
  }

  selectSuggestion(suggestionData, autoConfirm = false) {
    this.inputField.value = suggestionData.name;
    this.hiddenAddressIdInputField.value = suggestionData.global_address_id || '';
    this.hiddenCompanyIdInputField.value = suggestionData.company_id || '';

    this.inputField.classList.remove(this.errorClass);
    this.setHint();

    if (suggestionData.company_id !== null) {
      if (autoConfirm) {
        this.onConfirmJoinCompany();
      } else {
        this.requestJoinCompanyConfirmOverlay.open();
      }
    }
  }

  wait() {
    if (this.searchWaitTimeout) clearTimeout(this.searchWaitTimeout);
    return new Promise((resolve, reject) => {
      this.searchWaitTimeout = setTimeout(_ => resolve(), 250);
    });
  }

  validate() {
    const hints = [];
    const invalidFields = new Set();

    if (this.inputField.value.length < 2) {
      hints.push({
        type: 'alert',
        text: this.translations.companyEmptyError
      });
      invalidFields.add(this.inputField);
    }

    if (this.hiddenCompanyIdInputField.value) {
      hints.push({
        type: 'info',
        text: this.translations.joinCompanyInfo
      });
    }

    this.setHint(hints);

    [...invalidFields].map(field => field.classList.add(this.errorClass));
    return invalidFields.size === 0;
  }
}

class InputSuggestions extends Mediator {
  constructor(args) {
    super();

    this.inputField = args.inputField;
    this.focusClass = 'focus';
    this.instances = [];

    this.render();
    this.addEvents();

    const onWindowClick = function (e) {
      if (!document.body.contains(this.list)) {
        window.removeEventListener('click', this.onWindowClick);
        return;
      }

      if (!this.list.contains(e.target) && this.isOpen) {
        this.close();
      }
    }

    this.onWindowClick = onWindowClick.bind(this);
  }

  addEvents() {
    window.addEventListener('click', e => this.onWindowClick(e));

    ['keydown', 'keypress'].map(eventType => {
      this.inputField.addEventListener(eventType, e => {
        if (!this.list) return;

        const results = [...this.list.querySelectorAll('.suggestion')];
        const activeResult = this.list.querySelector(`.suggestion.${this.focusClass}`);
        const activeIndex = results.indexOf(activeResult);

        // arrow up
        if (e.key == 'Enter') {
          if (activeResult) {
            e.preventDefault();
            this.instances.map(instance => {
              if (instance.element.isEqualNode(activeResult)) {
                this.publish('select', instance.data);
              }
            })
          }
          this.close();
          return;
        }

        // arrow up
        if (e.key == 'ArrowUp') {
          e.preventDefault();
          if (activeResult) activeResult.classList.remove(this.focusClass);
          if (activeIndex > 0) results[activeIndex - 1].classList.add(this.focusClass);
        }

        // arrow down or tabulator
        if (e.key == 'ArrowDown' || e.key === 'Tab') {
          if (results[activeIndex + 1]) {
            e.preventDefault();
            if (activeResult) activeResult.classList.remove(this.focusClass);
            results[activeIndex + 1].classList.add(this.focusClass);
            return;
          }
        }
      });
    });
  }

  get isOpen() {
    return this.list && this.list.style.display === 'block';
  }

  open() {
    this.list.style.display = 'block';
  }

  close() {
    this.list.style.display = 'none';
    this.activeResult = null;
  }

  render(data = []) {
    if (this.list) this.list.parentElement.removeChild(this.list);
    this.list = document.createElement('div');
    this.list.classList.add('suggestions');
    this.inputField.insertAdjacentElement('afterend', this.list);

    this.instances = [];

    data.map(itemData => {
      const itemElement = document.createElement('div');
      this.list.insertAdjacentElement('beforeend', itemElement);

      this.instances.push(
        new Suggestion({
          parent: this,
          element: itemElement,
          data: itemData
        })
      );
    });

    if (data.length) this.open();
  }

  onSelect(suggestionData) {
    this.publish('select', suggestionData);
    this.close();
  }
}

class Suggestion {
  constructor(args) {
    this.parent = args.parent;
    this.element = args.element;
    this.data = args.data;

    this.render();
  }

  addEvents() {
    this.element.addEventListener('click', e => {
      this.parent.onSelect(this.data);
    });
  }

  render() {
    this.element.classList.add('suggestion');
    this.element.innerHTML = this.template;
    this.addEvents();
  }

  get template() {
    return `
      <div class="label">${this.data.name}</div>
      <div class="description">${this.data.city}, ${this.data.country_name}</div>
    `;
  }
}